import { useFormik } from "formik";
import * as Yup from "yup";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useState, useEffect, useRef } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import AppLoading from "../app-loading/app-loading";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ImageTemplateDemo } from "../images/file-upload-template";
import { Image } from "primereact/image";

interface InternalComplaintModel {
  id: number;
  complaintDescription: string;
  image: any;
  attachmentId: number;
  severity: string;
  createdBy: number;
  createdDate: string;
  updatedBy: number;
  updatedDate: string;
  customerName: string;
  createdByName: string;
  updatedByName: string;
  mobile: string;
}

interface UpdateInternalComplaintProps {
  isUpdate: boolean;
  handleOnSuccess: (updatedComplaint: InternalComplaintModel) => void;
  updateComplaintInfo: InternalComplaintModel;
}

const UpdateInternalComplaintDataTable: React.FC<UpdateInternalComplaintProps> = ({ 
  isUpdate, 
  handleOnSuccess, 
  updateComplaintInfo,
}) => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [loginUser, setLoginUser] = useState<any>();
  const [updateImage,setUpdateImage] = useState<any>();
  const [containerType,setContainerType] = useState<string>("Complaints");

  const severityOptions = [
    { label: "Low", value: "Low" },
    { label: "Medium", value: "Medium" },
    { label: "High", value: "High" },
  ];

  useEffect(() => {
    const LoginDetails = localStorage.getItem('user');
    if (LoginDetails != null) {
      setLoginUser(JSON.parse(LoginDetails));
    }

    formik.setValues({
      id: updateComplaintInfo.id || 0,
      complaintDescription: updateComplaintInfo.complaintDescription || "",
      severity: updateComplaintInfo.severity || "Medium",
      attachmentId: updateComplaintInfo.attachmentId || 0,
      image : updateComplaintInfo.image || null,
      createdBy: updateComplaintInfo.createdBy || 0,
      createdDate: updateComplaintInfo.createdDate || "",
      updatedBy: updateComplaintInfo.updatedBy || 0,
      updatedDate: updateComplaintInfo.updatedDate || "",
      customerName: updateComplaintInfo.customerName || "",
      createdByName: updateComplaintInfo.createdByName || "",
      updatedByName: updateComplaintInfo.updatedByName || "",
    });
  }, [updateComplaintInfo]);

  const formik = useFormik({
    initialValues: {
      id: updateComplaintInfo.id || 0,
      complaintDescription: updateComplaintInfo.complaintDescription || "",
      image : updateComplaintInfo.image || null,
      severity: updateComplaintInfo.severity || "Medium",
      attachmentId: updateComplaintInfo.attachmentId || 0,
      createdBy: updateComplaintInfo.createdBy || 0,
      createdDate: updateComplaintInfo.createdDate || "",
      updatedBy: updateComplaintInfo.updatedBy || 0,
      updatedDate: updateComplaintInfo.updatedDate || "",
      customerName: updateComplaintInfo.customerName || "",
      createdByName: updateComplaintInfo.createdByName || "",
      updatedByName: updateComplaintInfo.updatedByName || "",
    },
    validationSchema: Yup.object({
      complaintDescription: Yup.string().required("Description is required"),
      severity: Yup.string().required("Severity is required"),
      attachmentId: Yup.number()
        .min(0, "Attachment ID cannot be negative")
        .typeError("Attachment ID must be a number"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        const complaintData = {
          id: values.id,
          complaintDescription: values.complaintDescription,
          image:updateImage,
          severity: values.severity,
          attachmentId: values.attachmentId,
          updatedBy: loginUser?.userID || 0,
          updatedDate: new Date().toISOString(),
          updatedByName: loginUser?.name || "System",
          createdBy: values.createdBy,
          createdDate: values.createdDate,
          customerName: values.customerName,
          createdByName: values.createdByName,
          containerType: containerType
        };

        const response = await ComplaintService.UpdateInternalComplaint(complaintData);
        
        if (response) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Internal complaint updated successfully',
            life: 3000
          });
          
          handleOnSuccess({
            ...updateComplaintInfo,
            ...complaintData,
            complaintDescription: values.complaintDescription,
            severity: values.severity,
            attachmentId: values.attachmentId,
            updatedBy: loginUser?.userID || 0,
            updatedDate: new Date().toISOString(),
            updatedByName: loginUser?.name || "System",
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update internal complaint',
            life: 3000
          });
        }
      } catch (error) {
        console.error("Error updating internal complaint:", error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update internal complaint',
          life: 3000
        });
      } finally {
        setLoading(false);
      }
    }
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const inputFieldClasses = "w-full p-2 border-round border-1 border-300";
  const textareaClasses = `${inputFieldClasses} resize-none`;
  const dropdownClasses = `${inputFieldClasses} p-dropdown`;
  const readOnlyFieldClasses = `${inputFieldClasses} bg-gray-100`;

    const handelfileUpload = (query:any) => {
    const file = query;
    setUpdateImage(file)
  }

  const handelfileSizeExceeded = (message:string) => {
    toast.current?.show({
        severity: 'info',
        summary: 'InfoError',
        detail: message,
        life: 3000
    });
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12 flex align-items-stretch flex-wrap">
        <div className="flex flex-1 bg-white align-items-center justify-content-center">
          <form onSubmit={formik.handleSubmit} className="w-full">
            <h5 className="border-round mb-4 p-3 order-top-color p-2 panel-header-text text-center">
              Update Internal Complaint
            </h5>
            <div className="p-fluid formgrid grid">
              <div className="col-12">
                <div className="grid">
                  <div className="col-12 md:col-4 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="createdDate" className="block mb-2 font-bold">
                        Complaint Date
                      </label>
                      <InputText
                        id="createdDate"
                        name="createdDate"
                        value={formatDate(formik.values.createdDate)}
                        className={readOnlyFieldClasses}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="createdByName" className="block mb-2 font-bold">
                        Created By
                      </label>
                      <InputText
                        id="createdByName"
                        name="createdByName"
                        value={formik.values.createdByName}
                        className={readOnlyFieldClasses}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-12 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="complaintDescription" className="block mb-2 font-bold required-label">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <InputTextarea
                        id="complaintDescription"
                        name="complaintDescription"
                        rows={3}
                        value={formik.values.complaintDescription}
                        onChange={(e) => {
                          const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                          formik.setFieldValue('complaintDescription', cleanedValue);
                        }}
                        className={classNames(textareaClasses, { 
                          'p-invalid': formik.errors.complaintDescription && formik.touched.complaintDescription 
                        })}
                      />
                      {formik.errors.complaintDescription && formik.touched.complaintDescription && (
                        <small className="p-error block mt-1">{formik.errors.complaintDescription as string}</small>
                      )}
                    </div>
                  </div>
                  <div className="col-12 md:col-6 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="severity" className="block mb-2 font-bold required-label">
                        Severity <span className="text-red-500">*</span>
                      </label>
                      <Dropdown
                        id="severity"
                        name="severity"
                        options={severityOptions}
                        optionLabel="label"
                        optionValue="value"
                        value={formik.values.severity}
                        onChange={(e) => formik.setFieldValue("severity", e.value)}
                        className={classNames(dropdownClasses, { 
                          'p-invalid': formik.errors.severity && formik.touched.severity 
                        })}
                      />
                      {formik.errors.severity && formik.touched.severity && (
                        <small className="p-error block mt-1">{formik.errors.severity as string}</small>
                      )}
                    </div>
                  </div>
                    <ImageTemplateDemo onFileUpload={handelfileUpload} handelFileSize={handelfileSizeExceeded}/>
                    <div className="col-12 md:col-12 mb-3 px-3">
                    </div>
                    {formik.values.image?.map((x:any, index: number) => {
                      const fileUrl = x.fileDetails?.url;
                      const fileType = x.fileDetails?.type?.toLowerCase() || '';
                      const fileName = x.fileDetails?.name || `File-${index + 1}`;

                      const isImage = fileType.startsWith('image');
                      const isPDF = fileType === 'application/pdf';
                      const isVideo = fileType.startsWith('video');
                      const isExcel = fileType.includes('spreadsheet') || fileType.includes('excel');
                      const isWord = fileType.includes('word');
                      const isZip = fileType.includes('zip') || fileType.includes('compressed');

                      const iconClass = isExcel
                        ? 'pi pi-file-excel'
                        : isWord
                        ? 'pi pi-file-word'
                        : isZip
                        ? 'pi pi-file-archive'
                        : 'pi pi-file';

                      return (
                        <div key={index} className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                          <div className="card justify-content-center text-center p-3">

                            {/* ✅ Image Preview */}
                            {isImage && (
                              <>
                                <Image src={fileUrl} alt={fileName} width="250" preview />
                              </>
                            )}

                            {/* ✅ Video Preview */}
                            {isVideo && (
                              <>
                                <video controls width="100%" height="240">
                                  <source src={fileUrl} type="video/mp4" />
                                </video>
                              </>
                            )}

                            {/* ✅ PDF Preview */}
                            {isPDF && (
                              <>
                                <embed
                                  src={fileUrl}
                                  type="application/pdf"
                                  width="100%"
                                  height="300px"
                                  style={{ borderRadius: '6px', border: '1px solid #ccc' }}
                                />
                                <div className="mt-2">{fileName}</div>
                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="p-button p-button-sm p-button-text mt-2">
                                  Open PDF
                                </a>
                              </>
                            )}

                            {/* ✅ Other File Types */}
                            {!isImage && !isPDF && !isVideo && (
                              <>
                                <i className={iconClass} style={{ fontSize: '3rem', color: '#6b7280' }}></i>
                                <div className="mt-2">{fileName}</div>
                                <a href={fileUrl} download className="p-button p-button-sm p-button-outlined mt-2">
                                  Download
                                </a>
                              </>
                            )}

                          </div>
                        </div>
                      );
                    })}

                  

                  <div className="col-12 mt-4 px-3 flex justify-content-end">
                    <Button 
                      type="submit" 
                      label="Update Internal Complaint" 
                      className="p-button-primary w-full md:w-auto" 
                      disabled={loading}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {loading && <AppLoading />}
    </>
  );
};

export default UpdateInternalComplaintDataTable;